import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Group from '../../../components/form/elements/group';
import Form, { SubmitType } from '../../../components/form';
import { useHistory, useParams } from 'react-router-dom';
import { useService } from '../../../services/services';
import Link from '../../../components/form/elements/link';
import Button from '../../../components/form/elements/button';
import Title from '../../../components/common/title';
import Classifier from '../../../interfaces/classifier';
import ClassifierValue from '../../../interfaces/classifierValue';
import { ClassifierManager } from './libs/classifierManager';
import ViewClassifierValueInfo from '../../../pages/internal/classifiers/view-classifier-value-info';

export default function CreateClassifierValue() {
  const history = useHistory();
  const { t } = useTranslation();
  const { classifierId, parentValueId, groupKey } = useParams<{
    classifierId: string;
    parentValueId?: string;
    groupKey: string;
  }>();
  const { internalClassifierService } = useService();
  const [classifierValue, setClassifierValue] = useState(null);
  const [bodyElements, setBodyElements] = useState([]);
  const [footerElements, setFooterElements] = useState([]);
  const [title, setTitle] = useState('');
  const [backRoute, setBackRoute] = useState('');
  const [schema, setSchema] = useState(null);

  useEffect(() => {
    const subscription = internalClassifierService
      .classifier(groupKey, classifierId, parentValueId)
      .subscribe(async (classifier: Classifier) => {
        let cManager = new ClassifierManager(
          classifier,
          internalClassifierService,
          (key: string) => {
            return t(key);
          }
        );
        let backRoute = ClassifierManager.getBackRoute(
          classifier,
          parentValueId
            ? ({
                id: parentValueId,
                parent_id: parentValueId,
              } as ClassifierValue)
            : null
        );
        let classifierValue = { active: true } as ClassifierValue;

        setBodyElements([Group(await cManager.getFormFields())]);
        setFooterElements([Button('create'), Link(backRoute, 'cancel')]);

        setTitle(classifier.name);
        setBackRoute(backRoute);
        setSchema(cManager.getYupSchema());

        classifierValue.properties =
          cManager.getPropertyValues(classifierValue);

        setClassifierValue(classifierValue);
      });

    return () => subscription.unsubscribe();
  }, [
    parentValueId,
    internalClassifierService,
    setClassifierValue,
    setBodyElements,
    setFooterElements,
    setBackRoute,
    setTitle,
    classifierId,
    t,
    groupKey,
  ]);

  const submit = useCallback<SubmitType<ClassifierValue>>(
    (form) => [
      internalClassifierService.store(
        form,
        classifierId,
        groupKey,
        parentValueId
      ),
      () => {
        history.push(backRoute);
      },
    ],
    [
      internalClassifierService,
      backRoute,
      history,
      classifierId,
      groupKey,
      parentValueId,
    ]
  );

  return (
    <div>
      {parentValueId ? (
        <ViewClassifierValueInfo id={parentValueId} groupKey={groupKey} />
      ) : (
        <Title title={title} />
      )}
      <Form
        title={parentValueId ? title : t('classifiers.add_form_title')}
        mode={'create'}
        body={bodyElements}
        footer={footerElements}
        values={classifierValue}
        submit={submit}
        schema={schema}
      />
    </div>
  );
}
